import { query, serviceQueries } from '@estee/elc-service-bus';
import { UserInfo } from '../domain/entities/UserInfo';
import { ELCLogger, TriggerType } from '@estee/elc-logging';
import { API_REQUEST_TIMEOUT } from '../constants/Vto';

export interface IUserInfoResponse {
    signed_in: boolean;
    user_id: string;
    session_id?: string;
}

export interface IPromiseStack {
    resolve(value: UserInfo): void;
    reject(error?: Error): void;
}

export class UserInfoRepository {
    private userInfo: UserInfo;
    private logger: ELCLogger;
    private timeout: number;
    private promiseStack: IPromiseStack[] = [];
    private isRequestInProgress: boolean = false;

    constructor(serviceName: string, serviceVersion: string) {
        this.logger = new ELCLogger({ serviceName, serviceVersion });
    }

    public async getUser(): Promise<UserInfo> {
        if (this.userInfo) {
            return this.userInfo;
        }

        return this.getUserDetails();
    }

    public getUserId = async () => {
        const user = await this.getUser();

        if (user && user.isSignedIn) {
            return user.id;
        }
    };

    private async getUserDetails(): Promise<UserInfo> {
        // Prevent multi parallel duplicate API calls
        if (!this.isRequestInProgress) {
            clearTimeout(this.timeout);
            this.timeout = window.setTimeout(this.queryUserDetails, API_REQUEST_TIMEOUT);
        }

        return new Promise((resolve, reject) => {
            this.promiseStack.push({ resolve, reject });
        });
    }

    private queryUserDetails = async () => {
        try {
            this.isRequestInProgress = true;

            const data = await query(serviceQueries.GET_USER_DETAILS);

            this.isRequestInProgress = false;

            this.userInfo = UserInfo.fromJson((data as unknown) as IUserInfoResponse);

            this.handlePromise();
        } catch (e) {
            this.isRequestInProgress = false;
            this.handlePromise();

            this.logger.error({
                triggerType: TriggerType.runtimeError,
                message: `${e}`
            });
        }
    };

    private handlePromise() {
        this.promiseStack.forEach((promise) => {
            this.userInfo
                ? promise.resolve(this.userInfo)
                : promise.reject(new Error('No user info'));
        });

        this.promiseStack = [];
    }
}
